import { useState } from 'react';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import {
  useGetAllRepairServices,
  useUpdateManyRepairServices,
} from '@/hooks/repair-service.hooks';
import { useNavigate } from 'react-router-dom';
import { RepairServiceTableView } from '../components/RepairServiceTableView';
import { TRepairServiceSchema } from '@/schemas/repair-service/repair-service.schema';
import { RepairServiceTableHeaders } from '../components/RepairServiceTableHeaders';
import { RepairServiceTableData } from '../components/RepairServiceTableData';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { WarningMessage } from '@/components/Message/WarningMessage';

export const RepairServiceListPage = () => {
  const { t } = useTranslation('repairService');
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
  } = useDeactivateField<TRepairServiceSchema>({
    t,
    useUpdateData: useUpdateManyRepairServices,
  });

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };
  /**
   * === END OF Deactivate Data section ===
   */

  return (
    <>
      <PageHeader
        title={t('repairServices')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('repairServiceTypesForCostings')} />
        }
        actionListElement={
          <>
            <HeadingButton
              text={t('common:importAList')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
              }
              onClick={() => navigate('#')}
            />
            <HeadingButton
              text={t('addRepairService')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
              }
              onClick={() => navigate('/repair-service/create')}
            />
          </>
        }
      />
      <div>
        <RepairServiceTableView<TRepairServiceSchema>
          t={t}
          useGetAllData={useGetAllRepairServices}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={RepairServiceTableHeaders}
          tableData={RepairServiceTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
        />
      </div>
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
      >
        <WarningMessage message={t('common:unableToDeactivateLinkedItem')} />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans
              i18nKey="common:confirmDeactivateItems"
              components={{ br: <br /> }}
            />
          ) : (
            t('common:confirmDeactivateItem')
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
    </>
  );
};
